import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Sun, Moon } from "react-bootstrap-icons";
import styled from "styled-components";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    ReferenceLine
} from "recharts";
import type { ValueType } from "recharts/types/component/DefaultTooltipContent";

import { getAnalyticsWidget, createExportSchedule, getExportSchedules } from "../../utils/Requests";
import { getDefaultDateRange, getWidgetTheme } from "../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions, WidgetTheme } from "../../types/analytics";
import AnalyticsFiltersComponent from "../../components/analytics/AnalyticsFilters";
import { ExportButton, ScheduleExportModal } from "../../components/analytics";
import { ExportContext, ExportableData, ExportSchedule, ExportScheduleRequest } from "../../services/export";
import InteractiveLineChart from "../../components/analytics/InteractiveLineChart";
import Logger from "../../utils/Logger";

type KpiDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type KpiMetricCard = {
    key: string;
    label: string;
    value: number | null;
    changePercent: number | null;
    valueFormat?: "number" | "percent" | "decimal";
};

type SessionTrendPoint = {
    date: string;
    sessionCount: number;
    deviceCount: number;
    playCount: number;
    viewCount: number;
    userCount: number;
    playingUserCount: number;
    viewingUserCount: number;
};

type TopPlatformPoint = {
    devicePlatform: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type TopChannelPoint = {
    thingName: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type TopGenrePoint = {
    genreName: string;
    sessionCount: number;
    previousSessionCount: number;
    sessionPercentChange: number;
};

type KpiDashboardState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    isDarkTheme: boolean;
    trafficMetrics: KpiMetricCard[];
    userEngagementMetrics: KpiMetricCard[];
    sessionTrendData: SessionTrendPoint[];
    topPlatformsData: TopPlatformPoint[];
    showScheduleModal: boolean;
    schedules: ExportSchedule[];
    editingSchedule: ExportSchedule | null;
    topChannelsData: TopChannelPoint[];
    topGenresData: TopGenrePoint[];
};

const SingleRowMetricGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
`;

const MetricCard = styled.div<{ theme: WidgetTheme }>`
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    padding: 16px;
    background: ${props => props.theme.chartBg};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
`;

const MetricLabel = styled.div<{ theme: WidgetTheme }>`
    font-size: 0.9rem;
    color: ${props => props.theme.text};
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div<{ theme: WidgetTheme }>`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${props => props.theme.text};
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? "#28a745" : props.$negative ? "#dc3545" : "#6c757d"};
`;

const ChartContainer = styled.div<{ theme: WidgetTheme }>`
    padding: 16px;
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    background: ${props => props.theme.chartBg};
`;

const SectionContainer = styled.div`
    margin-top: 24px;
`;

const SectionHeader = styled.h5<{ theme: WidgetTheme }>`
    margin-bottom: 12px;
    color: ${props => props.theme.text};
`;

const TopKpiGroups = styled.div`
    display: flex;
    gap: 24px;
    align-items: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
    margin-top: 24px;
    padding-bottom: 4px;
`;

const KpiGroup = styled.div`
    min-width: fit-content;
`;

const SubChartBlock = styled.div<{ theme: WidgetTheme }>`
    margin-top: 18px;

    h6 {
        color: ${props => props.theme.text};
        margin-bottom: 8px;
        text-align: center;
    }
`;

const BackToBackCharts = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 12px;
`;

class KpiDashboard extends React.Component<KpiDashboardProps, KpiDashboardState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: KpiDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };

        this.state = {
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            filterKey: 0,
            isDarkTheme: true,
            trafficMetrics: [],
            userEngagementMetrics: [],
            sessionTrendData: [],
            topPlatformsData: [],
            topChannelsData: [],
            showScheduleModal: false,
            schedules: [],
            editingSchedule: null,
            topGenresData: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private getExportData = (): ExportableData[] => {
        const { trafficMetrics, userEngagementMetrics, sessionTrendData, topPlatformsData, topChannelsData, appliedFilters } = this.state;
        const data: ExportableData[] = [];

        // Traffic metrics
        if (trafficMetrics.length > 0) {
            data.push({
                title: "Traffic Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "Change %", format: "percent" }
                ],
                rows: trafficMetrics.map(m => ({ label: m.label, value: m.value, changePercent: m.changePercent })),
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // User engagement metrics
        if (userEngagementMetrics.length > 0) {
            data.push({
                title: "User Engagement Metrics",
                columns: [
                    { key: "label", label: "Metric" },
                    { key: "value", label: "Value", format: "number" },
                    { key: "changePercent", label: "Change %", format: "percent" }
                ],
                rows: userEngagementMetrics.map(m => ({ label: m.label, value: m.value, changePercent: m.changePercent })),
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Session trend data
        if (sessionTrendData.length > 0) {
            data.push({
                title: "Session Trend",
                columns: [
                    { key: "date", label: "Date", format: "date" },
                    { key: "sessionCount", label: "Sessions", format: "number" }
                ],
                rows: sessionTrendData,
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Top platforms
        if (topPlatformsData.length > 0) {
            data.push({
                title: "Top Platforms",
                columns: [
                    { key: "devicePlatform", label: "Platform" },
                    { key: "sessionCount", label: "Sessions", format: "number" },
                    { key: "sessionPercentChange", label: "Change %", format: "percent" }
                ],
                rows: topPlatformsData,
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        // Top channels
        if (topChannelsData.length > 0) {
            data.push({
                title: "Top Channels",
                columns: [
                    { key: "thingName", label: "Channel" },
                    { key: "sessionCount", label: "Sessions", format: "number" },
                    { key: "sessionPercentChange", label: "Change %", format: "percent" }
                ],
                rows: topChannelsData,
                metadata: { filters: appliedFilters, pageKey: "kpi_dashboard", customerSlug: this.props.customerConfig.slug }
            });
        }

        return data;
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "KPI Dashboard",
            pageKey: "kpi_dashboard",
            customerSlug: this.props.customerConfig.slug,
            filters: this.state.appliedFilters,
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleClick = (): void => {
        this.setState({ showScheduleModal: true, editingSchedule: null });
    };

    private handleScheduleModalHide = (): void => {
        this.setState({ showScheduleModal: false, editingSchedule: null });
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
        const response = await getExportSchedules(this.props.customerConfig);
        this.setState({ schedules: response.schedules });
    };

    private toTitleCase = (value: string): string => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    private formatMetricValue = (metric: KpiMetricCard): string => {
        if (metric.value === null || metric.value === undefined) {
            return "-";
        }

        if (metric.valueFormat === "percent") {
            return `${metric.value.toFixed(2)}%`;
        }

        if (metric.valueFormat === "decimal") {
            return metric.value.toFixed(2);
        }

        return metric.value.toLocaleString();
    };

    private formatChange = (changePercent: number | null): string => {
        if (changePercent === null || changePercent === undefined) {
            return "No change data";
        }

        const sign = changePercent > 0 ? "+" : "";
        return `${sign}${changePercent.toFixed(2)}%`;
    };

    private mapResponseToMetrics = (
        responseRow: Record<string, number>,
        preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" }>
    ): KpiMetricCard[] => {
        const preferred = preferredMetricMap
            .filter(metric => Object.prototype.hasOwnProperty.call(responseRow, metric.key))
            .map(metric => ({
                key: metric.key,
                label: metric.label,
                value: responseRow[metric.key] ?? null,
                changePercent: responseRow[metric.changeKey] ?? null,
                valueFormat: metric.valueFormat
            }));

        if (preferred.length > 0) {
            return preferred;
        }

        // Fallback: derive metric cards from *_percent_change pairs.
        return Object.keys(responseRow)
            .filter(key => !key.endsWith("_percent_change"))
            .map(key => {
                const changeKey = `${key}_percent_change`;
                return {
                    key,
                    label: this.toTitleCase(key),
                    value: responseRow[key] ?? null,
                    changePercent: responseRow[changeKey] ?? null,
                    valueFormat: key.endsWith("_percent") ? "percent" : "number"
                };
            });
    };

    private mapTrafficMetrics = (responseRow: Record<string, number>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" }> = [
            { key: "session_count", label: "Sessions", changeKey: "session_percent_change" },
            { key: "device_count", label: "Devices", changeKey: "device_percent_change" },
            { key: "user_count", label: "Users", changeKey: "user_percent_change" },
            { key: "play_count", label: "Plays", changeKey: "plays_percent_change" },
            { key: "playing_user_count", label: "Playing Users", changeKey: "playing_user_percent_change" },
            { key: "viewing_user_count", label: "Viewing Users", changeKey: "viewing_user_percent_change" },
            {
                key: "gdpr_status_device_percent",
                label: "GDPR Status Device %",
                changeKey: "gdpr_status_device_percent_change",
                valueFormat: "percent"
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap);
    };

    private mapUserEngagementMetrics = (responseRow: Record<string, number>): KpiMetricCard[] => {
        const preferredMetricMap: Array<{ key: string; label: string; changeKey: string; valueFormat?: "number" | "percent" | "decimal" }> = [
            {
                key: "sessions_per_user",
                label: "Sessions per User",
                changeKey: "sessions_per_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "plays_per_user",
                label: "Plays per User",
                changeKey: "plays_per_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "hours_watched",
                label: "Hours Watched",
                changeKey: "hours_watched_percent_change"
            },
            {
                key: "avg_hours_watched_per_playing_user",
                label: "Avg Hours per Playing User",
                changeKey: "avg_hours_watched_per_playing_user_percent_change",
                valueFormat: "decimal"
            },
            {
                key: "avg_completion_rate",
                label: "Avg Completion Rate",
                changeKey: "avg_completion_rate_percent_change",
                valueFormat: "percent"
            }
        ];

        return this.mapResponseToMetrics(responseRow, preferredMetricMap);
    };

    private formatTrendDate = (value: string): string => {
        try {
            const date = new Date(`${value}T00:00:00Z`);
            return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        } catch {
            return value;
        }
    };

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;
        
        try {
            this.setState({ isLoading: true, error: null });

            const [trafficResponse, engagementResponse, sessionTrendResponse, topPlatformsResponse, topThingsResponse, topGenresResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_traffic",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_user_engagement",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_traffic_trend",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_top_platforms",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_top_things",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "kpi_top_genres",
                    appliedFilters.startDate,
                    appliedFilters.endDate
                )
            ]);

            const trafficData = (trafficResponse as any)?.result;
            const trafficResultRow = Array.isArray(trafficData) && trafficData.length > 0 ? trafficData[0] : null;

            const engagementData = (engagementResponse as any)?.result;
            const engagementResultRow = Array.isArray(engagementData) && engagementData.length > 0 ? engagementData[0] : null;

            const trafficMetrics = trafficResultRow && typeof trafficResultRow === "object"
                ? this.mapTrafficMetrics(trafficResultRow as Record<string, number>)
                : [];

            const userEngagementMetrics = engagementResultRow && typeof engagementResultRow === "object"
                ? this.mapUserEngagementMetrics(engagementResultRow as Record<string, number>)
                : [];

            const trendDataRaw = (sessionTrendResponse as any)?.result;
            const sessionTrendData = (Array.isArray(trendDataRaw) ? trendDataRaw : [])
                .filter((row: Record<string, any>) => row?.date && typeof row?.session_count === "number")
                .map((row: Record<string, any>) => ({
                    date: row.date,
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    deviceCount: typeof row.device_count === "number" ? row.device_count : 0,
                    playCount: typeof row.play_count === "number" ? row.play_count : 0,
                    viewCount: typeof row.view_count === "number" ? row.view_count : 0,
                    userCount: typeof row.user_count === "number" ? row.user_count : 0,
                    playingUserCount: typeof row.playing_user_count === "number" ? row.playing_user_count : 0,
                    viewingUserCount: typeof row.viewing_user_count === "number" ? row.viewing_user_count : 0
                }))
                .sort((a: SessionTrendPoint, b: SessionTrendPoint) => a.date.localeCompare(b.date));

            const topPlatformsRaw = (topPlatformsResponse as any)?.result;
            const topPlatformsData = (Array.isArray(topPlatformsRaw) ? topPlatformsRaw : [])
                .filter((row: Record<string, any>) => row?.device_platform)
                .map((row: Record<string, any>) => ({
                    devicePlatform: row.device_platform,
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopPlatformPoint, b: TopPlatformPoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            const topThingsRaw = (topThingsResponse as any)?.result;
            const topChannelsData = (Array.isArray(topThingsRaw) ? topThingsRaw : [])
                .map((row: Record<string, any>) => ({
                    thingName: row.thing_name || "Unknown",
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopChannelPoint, b: TopChannelPoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            const topGenresRaw = (topGenresResponse as any)?.result;
            const topGenresData = (Array.isArray(topGenresRaw) ? topGenresRaw : [])
                .map((row: Record<string, any>) => ({
                    genreName: row.genre_name || "Unknown",
                    sessionCount: typeof row.session_count === "number" ? row.session_count : 0,
                    previousSessionCount: typeof row.previous_session_count === "number" ? row.previous_session_count : 0,
                    sessionPercentChange: typeof row.session_percent_change === "number" ? row.session_percent_change : 0
                }))
                .sort((a: TopGenrePoint, b: TopGenrePoint) => b.sessionCount - a.sessionCount)
                .slice(0, 10);

            this.setState({
                trafficMetrics,
                userEngagementMetrics,
                sessionTrendData,
                topPlatformsData,
                topChannelsData,
                topGenresData
            });
        } catch (err) {
            Logger.error("Error fetching KPI dashboard data", err);
            this.setState({ error: "Failed to fetch KPI dashboard data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private renderMetricCards = (metrics: KpiMetricCard[], theme: WidgetTheme): ReactNode => {
        if (metrics.length === 0) {
            return <Alert variant="info">No KPI data available for the selected date range.</Alert>;
        }

        return (
            <SingleRowMetricGrid>
                {metrics.map(metric => {
                    const positive = (metric.changePercent ?? 0) > 0;
                    const negative = (metric.changePercent ?? 0) < 0;

                    return (
                        <MetricCard key={metric.key} theme={theme} style={{ minWidth: "220px" }}>
                            <MetricLabel theme={theme}>{metric.label}</MetricLabel>
                            <MetricValue theme={theme}>{this.formatMetricValue(metric)}</MetricValue>
                            <MetricChange $positive={positive} $negative={negative}>
                                {this.formatChange(metric.changePercent)}
                            </MetricChange>
                        </MetricCard>
                    );
                })}
            </SingleRowMetricGrid>
        );
    };

    private renderSessionTrendChart = (theme: WidgetTheme): ReactNode => {
        const { sessionTrendData } = this.state;

        if (sessionTrendData.length === 0) {
            return <Alert variant="info" style={{ marginTop: "20px" }}>No session trend data available for the selected date range.</Alert>;
        }

        return (
            <SectionContainer>
                <InteractiveLineChart
                    title="Traffic Trends"
                    data={sessionTrendData}
                    xAxisDataKey="date"
                    metrics={[
                        { key: "sessionCount", name: "Sessions", color: "#007bff" },
                        { key: "deviceCount", name: "Devices", color: "#6f42c1" },
                        { key: "playCount", name: "Plays", color: "#fd7e14" },
                        { key: "viewCount", name: "Views", color: "#20c997" },
                        { key: "userCount", name: "Users", color: "#e83e8c" },
                        { key: "playingUserCount", name: "Playing Users", color: "#17a2b8" },
                        { key: "viewingUserCount", name: "Viewing Users", color: "#28a745" }
                    ]}
                    theme={theme}
                    height={320}
                    xAxisTickFormatter={this.formatTrendDate}
                    tooltipLabelFormatter={this.formatTrendDate}
                    tooltipValueFormatter={(value: number) => value.toLocaleString()}
                    yAxisLabel="Count"
                />
            </SectionContainer>
        );
    };

    private renderTopPlatformsSection = (theme: WidgetTheme): ReactNode => {
        const { topPlatformsData } = this.state;

        if (topPlatformsData.length === 0) {
            return (
                <SectionContainer>
                    <SectionHeader theme={theme}>Top Platforms</SectionHeader>
                    <Alert variant="info">No top platform data available for the selected date range.</Alert>
                </SectionContainer>
            );
        }

        return (
            <SectionContainer>
                <SectionHeader theme={theme}>Top Platforms</SectionHeader>
                <ChartContainer theme={theme}>
                    <SubChartBlock theme={theme}>
                        <BackToBackCharts>
                            <div>
                                <h6>Session Count</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topPlatformsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => value.toLocaleString()}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="devicePlatform"
                                            width={160}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                        />
                                        <Tooltip
                                            labelFormatter={(label: ReactNode) => String(label ?? "")}
                                            formatter={(value: ValueType | undefined) => [(value ?? 0).toLocaleString(), "Sessions"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionCount" fill="#007bff" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h6>Session % Change</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topPlatformsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            domain={[(dataMin: number) => Math.min(dataMin, 0), "auto"]}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => `${value}%`}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="devicePlatform"
                                            width={10}
                                            tick={false}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={false}
                                        />
                                        <ReferenceLine x={0} stroke={theme.text} strokeOpacity={0.5} />
                                        <Tooltip
                                            labelFormatter={(label: ReactNode) => String(label ?? "")}
                                            formatter={(value: ValueType | undefined) => [`${Number(value ?? 0).toFixed(2)}%`, "Session % Change"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionPercentChange">
                                            {topPlatformsData.map((entry: TopPlatformPoint) => (
                                                <Cell
                                                    key={`pct-${entry.devicePlatform}`}
                                                    fill={entry.sessionPercentChange >= 0 ? "#28a745" : "#dc3545"}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </BackToBackCharts>
                    </SubChartBlock>
                </ChartContainer>
            </SectionContainer>
        );
    };

    private renderTopChannelsSection = (theme: WidgetTheme): ReactNode => {
        const { topChannelsData } = this.state;

        if (topChannelsData.length === 0) {
            return (
                <SectionContainer>
                    <SectionHeader theme={theme}>Top Brands</SectionHeader>
                    <Alert variant="info">No top brands data available for the selected date range.</Alert>
                </SectionContainer>
            );
        }

        return (
            <SectionContainer>
                <SectionHeader theme={theme}>Top Brands</SectionHeader>
                <ChartContainer theme={theme}>
                    <SubChartBlock theme={theme}>
                        <BackToBackCharts>
                            <div>
                                <h6>Session Count</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topChannelsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => value.toLocaleString()}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="thingName"
                                            width={160}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                        />
                                        <Tooltip
                                            labelFormatter={(label: ReactNode) => String(label ?? "")}
                                            formatter={(value: ValueType | undefined) => [(value ?? 0).toLocaleString(), "Sessions"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionCount" fill="#007bff" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h6>Session % Change</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topChannelsData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            domain={[(dataMin: number) => Math.min(dataMin, 0), "auto"]}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => `${value}%`}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="thingName"
                                            width={10}
                                            tick={false}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={false}
                                        />
                                        <ReferenceLine x={0} stroke={theme.text} strokeOpacity={0.5} />
                                        <Tooltip
                                            labelFormatter={(label: ReactNode) => String(label ?? "")}
                                            formatter={(value: ValueType | undefined) => [`${Number(value ?? 0).toFixed(2)}%`, "Session % Change"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionPercentChange">
                                            {topChannelsData.map((entry: TopChannelPoint, index: number) => (
                                                <Cell
                                                    key={`channel-pct-${entry.thingName}-${index}`}
                                                    fill={entry.sessionPercentChange >= 0 ? "#28a745" : "#dc3545"}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </BackToBackCharts>
                    </SubChartBlock>
                </ChartContainer>
            </SectionContainer>
        );
    };

    private renderTopGenresSection = (theme: WidgetTheme): ReactNode => {
        const { topGenresData } = this.state;

        if (topGenresData.length === 0) {
            return (
                <SectionContainer>
                    <SectionHeader theme={theme}>Top Genres</SectionHeader>
                    <Alert variant="info">No top genre data available for the selected date range.</Alert>
                </SectionContainer>
            );
        }

        return (
            <SectionContainer>
                <SectionHeader theme={theme}>Top Genres</SectionHeader>
                <ChartContainer theme={theme}>
                    <SubChartBlock theme={theme}>
                        <BackToBackCharts>
                            <div>
                                <h6>Session Count</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topGenresData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => value.toLocaleString()}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="genreName"
                                            width={160}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                        />
                                        <Tooltip
                                            labelFormatter={(label: ReactNode) => String(label ?? "")}
                                            formatter={(value: ValueType | undefined) => [(value ?? 0).toLocaleString(), "Sessions"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionCount" fill="#007bff" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <div>
                                <h6>Session % Change</h6>
                                <ResponsiveContainer width="100%" height={320}>
                                    <BarChart
                                        data={topGenresData}
                                        layout="vertical"
                                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke={theme.border} strokeDasharray="3 3" />
                                        <XAxis
                                            type="number"
                                            domain={[(dataMin: number) => Math.min(dataMin, 0), "auto"]}
                                            tick={{ fill: theme.text }}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={{ stroke: theme.border }}
                                            tickFormatter={(value: number) => `${value}%`}
                                        />
                                        <YAxis
                                            type="category"
                                            dataKey="genreName"
                                            width={10}
                                            tick={false}
                                            axisLine={{ stroke: theme.border }}
                                            tickLine={false}
                                        />
                                        <ReferenceLine x={0} stroke={theme.text} strokeOpacity={0.5} />
                                        <Tooltip
                                            labelFormatter={(label: ReactNode) => String(label ?? "")}
                                            formatter={(value: ValueType | undefined) => [`${Number(value ?? 0).toFixed(2)}%`, "Session % Change"]}
                                            contentStyle={{ backgroundColor: theme.chartBg, border: `1px solid ${theme.border}`, color: theme.text }}
                                            itemStyle={{ color: theme.text }}
                                            labelStyle={{ color: theme.text }}
                                        />
                                        <Bar dataKey="sessionPercentChange">
                                            {topGenresData.map((entry: TopGenrePoint, index: number) => (
                                                <Cell
                                                    key={`genre-pct-${entry.genreName}-${index}`}
                                                    fill={entry.sessionPercentChange >= 0 ? "#28a745" : "#dc3545"}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </BackToBackCharts>
                    </SubChartBlock>
                </ChartContainer>
            </SectionContainer>
        );
    };

    public render(): ReactNode {
        const { isLoading, error, filters, filterOptions, isDarkTheme, trafficMetrics, userEngagementMetrics } = this.state;
        const theme = getWidgetTheme(isDarkTheme);

        return (
            <div ref={this.containerRef}>
                <Container className="mw-100" key="kpi-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && (trafficMetrics.length > 0 || userEngagementMetrics.length > 0)}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={this.handleScheduleClick}
                        />
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>KPI Dashboard</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                    />

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "40px" }}>
                                <Spinner animation="border" variant="primary" />
                                <p style={{ marginTop: "10px", color: theme.text }}>Loading KPI dashboard...</p>
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <TopKpiGroups>
                                <KpiGroup>
                                    <SectionHeader theme={theme}>Traffic</SectionHeader>
                                    {this.renderMetricCards(trafficMetrics, theme)}
                                </KpiGroup>    
                            </TopKpiGroups>
                            <TopKpiGroups>
                                <KpiGroup>
                                    <SectionHeader theme={theme}>User Engagement</SectionHeader>
                                    {this.renderMetricCards(userEngagementMetrics, theme)}
                                </KpiGroup>
                            </TopKpiGroups>
                            <Row>
                                <Col>
                                    {this.renderSessionTrendChart(theme)}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12} lg={6} xl={4}>
                                    {this.renderTopPlatformsSection(theme)}
                                </Col>
                                <Col md={12} lg={6} xl={4}>
                                    {this.renderTopChannelsSection(theme)}
                                </Col>
                                <Col md={12} lg={6} xl={4}>
                                    {this.renderTopGenresSection(theme)}
                                </Col>
                            </Row>
                        </>
                    )}

                    <ScheduleExportModal
                        show={this.state.showScheduleModal}
                        onHide={this.handleScheduleModalHide}
                        filters={this.state.appliedFilters}
                        pageKey="kpi_dashboard"
                        pageTitle="KPI Dashboard"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={this.state.editingSchedule}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
            </div>
        );
    }
}

export default KpiDashboard;
